import { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase/config";
import { useAuth } from "./useAuth";
import {
  ADMIN_EMAILS,
  DEFAULT_SUBSCRIPTION,
  FEATURE_LIMITS,
  WHITELIST_EMAILS,
} from "../constants/pricing";

// Normalizza un valore-data (Firestore Timestamp | Date | number) in millis.
function toMillis(value) {
  if (!value) return null;
  if (typeof value.toMillis === "function") return value.toMillis(); // Firestore Timestamp
  if (value instanceof Date) return value.getTime();
  if (typeof value === "number") return value;
  return null;
}

// Estrae solo i campi abbonamento dal doc utente, con fallback ai default.
function readSubscription(data) {
  return {
    plan: data.plan || DEFAULT_SUBSCRIPTION.plan,
    subscriptionStatus: data.subscriptionStatus || DEFAULT_SUBSCRIPTION.subscriptionStatus,
    billingCycle: data.billingCycle || DEFAULT_SUBSCRIPTION.billingCycle,
    trialEndsAt: data.trialEndsAt ?? null,
    currentPeriodEnd: data.currentPeriodEnd ?? null,
    stripeCustomerId: data.stripeCustomerId ?? null,
    stripeSubscriptionId: data.stripeSubscriptionId ?? null,
  };
}

/**
 * Hook abbonamento: legge in real-time users/{uid} e deriva lo stato del piano.
 * @returns {{ plan, subscriptionStatus, billingCycle, daysLeft, isTrialExpired, isPro, loading, subscription }}
 */
export function useSubscription() {
  const { user, loading: authLoading } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [whitelistDoc, setWhitelistDoc] = useState(null);
  const [whitelistLoading, setWhitelistLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setSubscription(null);
      setLoading(false);
      return;
    }

    const ref = doc(db, "users", user.uid);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const data = snap.exists() ? snap.data() : {};
        setSubscription(readSubscription(data));
        setLoading(false);
      },
      (err) => {
        console.warn("useSubscription snapshot error:", err?.message || err);
        setSubscription({ ...DEFAULT_SUBSCRIPTION });
        setLoading(false);
      }
    );

    return () => unsub();
  }, [user, authLoading]);

  // Whitelist dinamica: legge whitelist/{email} (doc id = email in minuscolo).
  // Additivo e isolato dal primo effect: un errore qui non deve mai rompere
  // la logica abbonamento standard, quindi in caso di errore/assenza si
  // ricade semplicemente sul fallback statico WHITELIST_EMAILS.
  useEffect(() => {
    if (authLoading) return;
    const email = (user?.email || "").toLowerCase();
    if (!email) {
      setWhitelistDoc(null);
      setWhitelistLoading(false);
      return;
    }

    const ref = doc(db, "whitelist", email);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        setWhitelistDoc(snap.exists() ? snap.data() : null);
        setWhitelistLoading(false);
      },
      (err) => {
        console.warn("useSubscription whitelist snapshot error:", err?.message || err);
        setWhitelistDoc(null);
        setWhitelistLoading(false);
      }
    );

    return () => unsub();
  }, [user, authLoading]);

  const sub = subscription || DEFAULT_SUBSCRIPTION;
  const plan = sub.plan || "trial";
  const trialEndMs = toMillis(sub.trialEndsAt);
  const now = Date.now();

  const daysLeft =
    trialEndMs != null ? Math.max(0, Math.ceil((trialEndMs - now) / 86400000)) : null;
  let isTrialExpired = plan === "trial" && trialEndMs != null && trialEndMs < now;
  let isPro = plan === "pro" && sub.subscriptionStatus === "active";

  // Tier EFFETTIVO usato per il gating: il "free" è lo stato ristretto post-trial
  // o abbonamento non attivo. Il trial non scaduto ha accesso pieno (limiti trial=pro).
  let tier =
    plan === "enterprise"
      ? "enterprise"
      : plan === "pro"
      ? sub.subscriptionStatus === "active"
        ? "pro"
        : "free"
      : plan === "trial"
      ? isTrialExpired
        ? "free"
        : "trial"
      : "free";

  // --- Override admin / whitelist -----------------------------------------
  // Fonte di verità admin: ADMIN_EMAILS in constants/pricing.js (client-side,
  // solo per UI/UX; le firestore.rules e le Cloud Function usano users/{uid}.role).
  const email = (user?.email || "").toLowerCase();
  const isAdmin = ADMIN_EMAILS.map((e) => e.toLowerCase()).includes(email);

  // Whitelist statica (fallback) OR whitelist dinamica (collection Firestore).
  const inStaticWhitelist = WHITELIST_EMAILS.map((e) => e.toLowerCase()).includes(email);
  const whitelistExpiresMs = whitelistDoc ? toMillis(whitelistDoc.expiresAt) : null;
  const whitelistNotExpired = whitelistExpiresMs == null || whitelistExpiresMs > now;
  const inDynamicWhitelist = Boolean(whitelistDoc) && whitelistNotExpired;
  const isWhitelisted = inStaticWhitelist || inDynamicWhitelist;

  if (isAdmin || isWhitelisted) {
    // Bypass totale: Pro forzato, nessun trial scaduto, tier pieno.
    isPro = true;
    isTrialExpired = false;
    tier = "pro";
  }

  const limits = FEATURE_LIMITS[tier] || FEATURE_LIMITS.free;

  // true se il tier effettivo abilita la feature indicata.
  const canAccessFeature = (featureName) => Boolean(limits[featureName]);
  // numero di fatture consentite (Infinity per i tier pieni).
  const getInvoiceLimit = () => limits.invoicesPerMonth;

  return {
    plan,
    tier,
    subscriptionStatus: sub.subscriptionStatus,
    billingCycle: sub.billingCycle,
    daysLeft,
    isTrialExpired,
    isPro,
    isAdmin,
    isWhitelisted,
    limits,
    canAccessFeature,
    getInvoiceLimit,
    loading: authLoading || loading || whitelistLoading,
    subscription: sub,
  };
}
